import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { getDb, getFirebaseStorage } from "./firebase";
import { useAuth } from "./auth-context";
import {
  type Car,
  type CarDoc,
  type CarNotif,
  type RawNotifDoc,
  toCarNotif,
} from "./cars";
import { getBrandById, getModelById } from "./static/cars";
import { getCarCategoryName } from "./static/car-categories";

function loadBrandName(brandId: string): string | null {
  return getBrandById(brandId)?.name ?? null;
}

function loadModelName(brandId: string, modelId: string): string | null {
  return getModelById(brandId, modelId)?.model ?? null;
}

function loadCategoryName(categoryId: string): string | null {
  return getCarCategoryName(categoryId);
}

const imageUrlCache = new Map<string, string | null>();

async function loadImageUrl(filePath: string): Promise<string | null> {
  if (imageUrlCache.has(filePath)) return imageUrlCache.get(filePath) ?? null;
  try {
    const url = await getDownloadURL(storageRef(getFirebaseStorage(), filePath));
    imageUrlCache.set(filePath, url);
    return url;
  } catch {
    imageUrlCache.set(filePath, null);
    return null;
  }
}

type SnapshotState = {
  uid: string | null;
  rawCars: CarDoc[] | null;
  notifications: CarNotif[];
};

type EnrichmentState = {
  forSnapshot: CarDoc[] | null;
  cars: Car[];
};

export type UseUserDataState = {
  cars: Car[];
  notifications: CarNotif[];
  loading: boolean;
};

export function useUserData(): UseUserDataState {
  const { user, loading: authLoading } = useAuth();
  const [snap, setSnap] = useState<SnapshotState>({
    uid: null,
    rawCars: null,
    notifications: [],
  });
  const [enriched, setEnriched] = useState<EnrichmentState>({
    forSnapshot: null,
    cars: [],
  });

  useEffect(() => {
    if (!user) return;
    const uid = user.uid;
    const db = getDb();
    const unsubCars = onSnapshot(
      collection(db, "userData", uid, "cars"),
      (s) => {
        const list: CarDoc[] = s.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<CarDoc, "id">),
        }));
        setSnap((prev) => ({
          uid,
          rawCars: list,
          notifications: prev.uid === uid ? prev.notifications : [],
        }));
      }
    );
    const unsubNotifs = onSnapshot(
      collection(db, "userData", uid, "notifications"),
      (s) => {
        const list: CarNotif[] = s.docs.map((d) =>
          toCarNotif({ id: d.id, ...(d.data() as Omit<RawNotifDoc, "id">) })
        );
        setSnap((prev) => ({
          uid,
          rawCars: prev.uid === uid ? prev.rawCars : null,
          notifications: list,
        }));
      }
    );
    return () => {
      unsubCars();
      unsubNotifs();
    };
  }, [user]);

  const isCurrent = !!user && snap.uid === user.uid;
  const rawCars = isCurrent ? snap.rawCars : null;
  const notifications = isCurrent ? snap.notifications : [];

  useEffect(() => {
    if (!rawCars) return;
    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(
        rawCars.map(async (c) => {
          const filePath = c.image?.filePath?.trim() || null;
          const imageUrl = filePath ? await loadImageUrl(filePath) : null;
          const car: Car = {
            ...c,
            brandName: c.brandId ? loadBrandName(c.brandId) : null,
            modelName:
              c.brandId && c.modelId ? loadModelName(c.brandId, c.modelId) : null,
            categoryName: c.categoryId ? loadCategoryName(c.categoryId) : null,
            imageUrl,
          };
          return car;
        })
      );
      if (!cancelled) {
        setEnriched({ forSnapshot: rawCars, cars: resolved });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rawCars]);

  const enrichmentReady = enriched.forSnapshot === rawCars && rawCars !== null;
  const cars = enrichmentReady ? enriched.cars : [];

  return {
    cars,
    notifications,
    // Stay loading until image-URL enrichment catches up with the latest
    // snapshot. Otherwise consumers see a render where `loading === false`
    // but `cars === []`, which made CumparaRcaDate redirect to /produse mid-
    // checkout (e.g. browser back from /contract, or fresh pick from the car
    // picker) because `cars.find(...)` came back undefined.
    loading: authLoading || rawCars === null || !enrichmentReady,
  };
}
